/** What the tour's panels and widgets read besides the draft: the loaded detail (stored
 *  images, names of picked records) and its baseline, the page's staged files, the names
 *  of the rows' categories (and the itinerary's activities), the link context, and the
 *  itinerary workspace's page-level parts (its issues store, the ledger's focus requests).
 *  One provider for the page; its value changes only with the loaded tour. */
import { createContext, useContext, type ReactNode } from 'react';

import type { TourDetail } from '@/api/tours';
import type { StagedFiles } from '@/features/records/fields/widgets/stagedFiles';
import type { OutlineFocusChannel } from '@/features/records/outline/bridge';
import type { OutlineIssues } from '@/features/records/outline/issues';

import type { TourCtx } from '../tourLinks';

import type { TourBaseline } from './baselines';

import type { TourNames } from './names';

export interface TourRecordData {
  detail: TourDetail;
  /** The detail's draft baselines (memoised on `updated_ts`). */
  baseline: TourBaseline;
  files: StagedFiles;
  links: TourCtx;
  /** Category names of the rows (stored and picked). */
  names: TourNames;
  /** The itinerary's rules' and the server's messages per input. */
  itineraryIssues: OutlineIssues;
  /** A ledger row asking for an itinerary field that is not on screen. */
  itineraryFocus: OutlineFocusChannel;
}

const Ctx = createContext<TourRecordData | null>(null);

export function TourRecordProvider({ value, children }: { value: TourRecordData; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTourRecord(): TourRecordData {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTourRecord must be used inside <TourRecordProvider>');
  return v;
}
